"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import LanguageSwitcher from "./LanguageSwitcher";

interface MenuItem {
  href: string;
  label: string;
}

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
  isLoggedIn?: boolean;
  userName?: string;
  onSignOut?: () => void;
}

export default function MobileDrawer({
  isOpen,
  onClose,
  menuItems,
  isLoggedIn = false,
  userName = "User",
  onSignOut,
}: MobileDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("common");

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const firstFocusable = drawerRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-secondary-900/50 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        id="mobile-menu"
        className={`fixed top-0 left-0 z-50 h-full w-72 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-out md:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-200">
          <Link href="/" onClick={onClose} aria-label="Real Impeks - Home">
            <Image
              src="/images/logo.png"
              alt="Real Impeks"
              width={275}
              height={79}
              className="h-8 w-auto"
            />
          </Link>
          <button
            type="button"
            className="p-2 -mr-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors"
            onClick={onClose}
            aria-label={t("closeMenu")}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* User Info - If logged in */}
        {isLoggedIn && (
          <div className="px-4 py-3 border-b border-secondary-200 bg-secondary-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold">
                {userName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-secondary-900">{userName}</p>
                <p className="text-xs text-secondary-500">{t("loggedIn") || "Prijavljeni ste"}</p>
              </div>
            </div>
          </div>
        )}

        {/* Language Switcher - Mobile */}
        <div className="px-4 py-3 border-b border-secondary-200">
          <LanguageSwitcher />
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto py-4" aria-label="Mobile menu">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="flex items-center px-4 py-3 text-base font-medium text-secondary-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={onClose}
                >
                  {item.label}
                </Link>
              </li>
            ))}

            {/* Dashboard link for logged in users */}
            {isLoggedIn && (
              <li>
                <Link
                  href="/dashboard"
                  className="flex items-center gap-3 px-4 py-3 text-base font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={onClose}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  {t("dashboard") || "Dashboard"}
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Auth Section - Footer */}
        {!isLoggedIn ? (
          <div className="border-t border-secondary-200 p-4 space-y-2">
            <Link
              href="/login"
              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-secondary-700 border border-secondary-300 hover:border-primary-500 hover:text-primary-500 rounded-lg transition-colors"
              onClick={onClose}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
              {t("login")}
            </Link>
            <Link
              href="/register"
              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
              onClick={onClose}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
              {t("register")}
            </Link>
          </div>
        ) : (
          <div className="border-t border-secondary-200 p-4">
            <button
              onClick={() => {
                onClose();
                onSignOut?.();
              }}
              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t("logout") || "Odjavi se"}
            </button>
          </div>
        )}

        {/* Company Info */}
        <div className="border-t border-secondary-200 p-4">
          <p className="text-xs text-secondary-400 text-center">
            © {new Date().getFullYear()} Real Impeks
          </p>
          <p className="text-xs text-secondary-400 text-center mt-1">
            www.realimpeks.co.rs
          </p>
        </div>
      </aside>
    </>
  );
}
